import { useState } from 'react';
import {
  FaSwimmingPool, FaGasPump, FaTint, FaBuilding, FaBasketballBall,
  FaTableTennis, FaDumbbell, FaChess, FaChild, FaHospitalAlt,
  FaShoppingBasket, FaStore, FaSchool, FaMapMarkerAlt,
  FaBed, FaBath, FaDoorOpen, FaClock, FaPhone, FaEnvelope,
  FaWhatsapp, FaUserShield, FaHandshake
} from 'react-icons/fa';
import MediaGallery from '../../components/media/MediaGallery';
import Avatar from '../../components/notes/Avatar';
import DOMPurify from 'dompurify';
import TimeAgo from '../../components/timeAgo/TimeAgo'; 

const featureIcons = {
  swimming_pool: { icon: <FaSwimmingPool />, label: 'Swimming Pool' },
  gas_connection: { icon: <FaGasPump />, label: 'Gas Connection' },
  ro: { icon: <FaTint />, label: 'RO Water' },
  club_house: { icon: <FaBuilding />, label: 'Club House' },
  basketball_court: { icon: <FaBasketballBall />, label: 'Basketball Court' },
  tennis_court: { icon: <FaTableTennis />, label: 'Tennis Court' },
  gym: { icon: <FaDumbbell />, label: 'Gym' },
  indoor_games: { icon: <FaChess />, label: 'Indoor Games' },
  child_play_area: { icon: <FaChild />, label: 'Child Play Area' },
  hospital_near_by: { icon: <FaHospitalAlt />, label: 'Hospital Nearby' },
  mall_near_by: { icon: <FaShoppingBasket />, label: 'Mall Nearby' },
  market_near_by: { icon: <FaStore />, label: 'Market Nearby' },
  school_near_by: { icon: <FaSchool />, label: 'School Nearby' },
};

const PropertyDetailCard = ({
  name,
  address,
  land_mark,
  area,
  pin_code,
  city,
  state,
  country,
  structure,
  description,
  feature, 
  price,
  propertyPurpose,
  propertyType, 
  property_sub_type,
  property_sub_sub_type,
  photo,
  creater,
  images = [],
  videos = [],
  attachments = [],
  createdOn,
  isPublished,
  user_type,
  is_address_added,
  is_become_a_partner,
  is_email_verified,
  is_phone_verified,
  is_whatsapp_number,
  isAdmin

}) => {
  const [showFullDescription, setShowFullDescription] = useState(false);

  const descriptionWords = description.trim().split(' ');
  const isLongDescription = descriptionWords.length > 50;
  const displayedDescription =
    isLongDescription && !showFullDescription
      ? descriptionWords.slice(0, 50).join(' ') + '...'
      : description;

  return (
    <div className="card shadow p-4 mb-5 bg-white rounded">
      
      {/* Media */}
      <MediaGallery
        images={images}
        promotion_videos={videos}
        attachments={attachments}
      />

      {/* Creator Info */}
      <div className="mt-5 p-3 ">
        <div className="row align-items-center">
          
          {/* Left: Creator Info */}
          <div className="col-md-6 d-flex align-items-center">
            <Avatar src={photo} icon={creater} size={48} />
            <div className="ms-3">
              <div className="text-muted small">Listed By</div>
              <div className="fw-semibold text-dark">{creater}</div>
            </div>
          </div>

          {/* Right: Created + Published Info */}
          <div className="col-md-6 d-flex flex-column align-items-md-end mt-3 mt-md-0">
            <div className="d-flex align-items-center mb-1">
              <FaClock className="me-2 text-secondary" />
              <div className="text-muted small">
                Created <TimeAgo timestamp={createdOn} />
              </div>
            </div>
            {isAdmin && 
              <div className="d-flex align-items-center">
                <span
                  className={`badge ${
                    isPublished ? 'bg-success' : 'bg-danger'
                  } text-white fs-6`} 
                >
                  {isPublished ? 'Published' : 'Not Published'}
                </span>
              </div>
            }
          </div>

        </div>
      </div>

      {/* User Info Section */}
      {isAdmin && 
        <div className="card border mt-4 shadow-sm p-4 mb-4">
          <h5 className="fw-bold mb-4">User Summary</h5>

          <div className="row gy-4">

            {/* User Type */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaUserShield className="me-3 text-primary fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">User Type</div>
                  <div className="fw-medium">{user_type}</div>
                </div>
              </div>
            </div>

            {/* Email Verified */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaEnvelope className="me-3 text-secondary fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">Email Status</div>
                  <div className={`badge rounded-pill px-3 py-1 fw-medium ${is_email_verified ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-dark'}`}>
                    {is_email_verified ? 'Verified' : 'Pending Verification'}
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Verified */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaPhone className="me-3 text-secondary fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">Phone Status</div>
                  <div className={`badge rounded-pill px-3 py-1 fw-medium ${is_phone_verified ? 'bg-success-subtle text-success' : 'bg-warning-subtle text-dark'}`}>
                    {is_phone_verified ? 'Verified' : 'Not Verified'}
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaWhatsapp className="me-3 text-success fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">WhatsApp Number</div>
                  <div className={`badge rounded-pill px-3 py-1 fw-medium ${is_whatsapp_number ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-muted'}`}>
                    {is_whatsapp_number ? 'Available' : 'Not Provided'}
                  </div>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaMapMarkerAlt className="me-3 text-danger fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">Address Status</div>
                  <div className={`badge rounded-pill px-3 py-1 fw-medium ${is_address_added ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                    {is_address_added ? 'Added' : 'Missing'}
                  </div>
                </div>
              </div>
            </div>

            {/* Partner Status */}
            <div className="col-md-6">
              <div className="d-flex align-items-start">
                <FaHandshake className="me-3 text-info fs-5 mt-1" />
                <div>
                  <div className="text-muted small fw-semibold mb-1">Partner Program</div>
                  <div className={`badge rounded-pill px-3 py-1 fw-medium ${is_become_a_partner ? 'bg-info-subtle text-info' : 'bg-light text-muted border'}`}>
                    {is_become_a_partner ? 'Partner Approved' : 'Application Incomplete'}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      }

      {/* Title and Location */}
      <div className="mt-4">
        <h3 className="fw-bold mb-2">{name}</h3>

        <div className="text-muted mb-3">
          <div className="d-flex align-items-center mb-1">
            <FaMapMarkerAlt className="me-2 text-secondary" />
            <span>{address}</span>
          </div>
          {land_mark && (
            <div className="ms-4 text-muted small">Landmark: {land_mark}</div>
          )}
          <div className="ms-4 text-muted small">
            {area} 
          </div>
          <div className="ms-4 text-muted small">
            {city}, {state}, {country} - {pin_code}
          </div>
        </div>

        {/* Price */}
        <h4 className="text-success fw-semibold mt-2 mb-3">
          ₹ {price.toLocaleString()}
        </h4>

        {/* Property Type Hierarchy */}
        <div className="mb-4">
          <div className="d-flex flex-wrap gap-3 fs-3">
              <span className="badge bg-primary-subtle text-primary border">
                {propertyPurpose}
              </span>
              <span className="badge bg-primary-subtle text-primary border">
                {propertyType}
              </span>
              <span className="badge bg-primary-subtle text-primary border">
                {property_sub_type}
              </span>
              <span className="badge bg-primary-subtle text-primary border">
                {property_sub_sub_type}
              </span>
          </div>
        </div>

        {/* Structure Details */}
        {structure && (
          <div className="d-flex flex-wrap gap-3 mb-4">
            {structure?.bed_rooms && (
              <span className="d-flex align-items-center bg-light border rounded px-3 py-2">
                <FaBed className="me-2 text-primary" />
                {structure.bed_rooms} Bedroom{structure.bed_rooms > 1 ? 's' : ''}
              </span>
            )}
            {structure?.bath_rooms && (
              <span className="d-flex align-items-center bg-light border rounded px-3 py-2">
                <FaBath className="me-2 text-primary" />
                {structure.bath_rooms} Bathroom{structure.bath_rooms > 1 ? 's' : ''}
              </span>
            )}
            {structure?.rooms && (
              <span className="d-flex align-items-center bg-light border rounded px-3 py-2">
                <FaDoorOpen className="me-2 text-primary" />
                {structure.rooms} Room{structure.rooms > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>



      {/* Description */}
      <div className="mt-3">
        <h5 className="text-muted">Description</h5>

        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(displayedDescription),
          }}
        />

        {isLongDescription && (
          <button
            className="btn btn-link p-0 bg-white border-0"
            onClick={() => setShowFullDescription(!showFullDescription)}
          >
            {showFullDescription ? 'Read Less' : 'Read More'}
          </button>
        )}
      </div>

      {/* Features */}
      {feature && (
        <div className="mt-5">
          <h5 className="mb-4 fw-bold text-dark">Facilities & Amenities</h5>
          <div className="row g-3">
            {Object.entries(featureIcons).map(([key, { icon, label }]) =>
              feature[key] ? (
                <div
                  key={key}
                  className="col-6 col-md-4 col-lg-3"
                >
                  <div className="d-flex align-items-center p-3 border rounded shadow-sm bg-white h-100">
                    <div className="me-3 text-primary" style={{ fontSize: '1.5rem' }}>
                      {icon}
                    </div>
                    <div className="flex-grow-1 text-dark fw-medium">{label}</div>
                  </div>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default PropertyDetailCard;
